import { liveGames$ } from '.';
const liveGamesStorage = (() => {
  let events = [];

  const addOrUpdate = (json) => {
    const g = json?.obj?.[0];
    const matchId = String(g?.std ?? '');
    if (!matchId) return;

    g.std = matchId;
    g.matchId = matchId;

    const index = events.findIndex(x => String(x?.obj?.[0]?.std) === matchId);

    if (index > -1) {
      const prevEvent = events[index];
      const prevGame  = prevEvent?.obj?.[0] || {};

      const before = Array.isArray(prevGame?.sc?.GOAL) ? prevGame.sc.GOAL : null;
      const after  = Array.isArray(g?.sc?.GOAL) ? g.sc.GOAL : null;

      const scoreChanged =
        Array.isArray(after) &&
        (!Array.isArray(before) || before[0] !== after[0] || before[1] !== after[1]);

      const preserved = {
        goalFlashUntil: prevGame.goalFlashUntil,
        goalMessage: prevGame.goalMessage,
      };

      if (scoreChanged) {
        preserved.goalFlashUntil = Date.now() + 8000;
        preserved.goalMessage = 'GOAL!';
      }

      const nextGame  = { ...g, ...preserved };
      const nextEvent = { ...json, obj: [nextGame] };

      const next = events.slice();
      next[index] = nextEvent;
      events = next;
    } else {
      events = [...events, json];
    }

    liveGames$.next(events);
  };

  const removeEvent = (matchId) => {
    events = events.filter(x => String(x?.obj?.[0]?.std) !== String(matchId));
    liveGames$.next(events);
  };
	
	  // --- odds (options) deep-merge helpers ---
const ENABLE_ODDS_LOG = true;
function optionKey(o) {
  // Prefer a stable id if you have it (e.g., o.k). Fallback to name.
  return String(o?.k ?? o?.n ?? "");
}

function mergeOptions(prevOpts = [], patchOpts = [], logChanges = ENABLE_ODDS_LOG) {
  const map = new Map();
  prevOpts.forEach(opt => map.set(optionKey(opt), { ...opt })); // preserve existing

  if (Array.isArray(patchOpts)) {
    patchOpts.forEach(p => {
      const key = optionKey(p);
      const prev = map.get(key);
      if (prev) {
        if (logChanges && "v" in p && prev.v !== p.v) {
        //  console.log(`[ODD CHANGED] ${prev.n ?? key}: ${prev.v} → ${p.v}`);
        }
        map.set(key, { ...prev, ...p }); // merge only provided fields
      } else {
        map.set(key, { ...p });          // new option
      }
    });
  }

  // keep original order, then append new ones in patch order
  const existingKeys = prevOpts.map(optionKey);
  const merged = [];
  existingKeys.forEach(k => merged.push(map.get(k)));
  if (Array.isArray(patchOpts)) {
    patchOpts.forEach(p => {
      const k = optionKey(p);
      if (!existingKeys.includes(k)) merged.push(map.get(k));
    });
  }
  return merged;
}

function mergeMarket(prevMarket = {}, patchMarket = {}) {
  const { o: patchOptions, ...patchRest } = patchMarket;
  const { o: prevOptions,  ...prevRest  } = prevMarket;

  return {
    ...prevRest,
    ...patchRest,
    o: mergeOptions(prevOptions, patchOptions),
  };
}

// --- scores deep-merge + derived display helpers ---
function deepMergeScores(prev = {}, patch = {}) {
  const out = { ...prev };
  for (const k of Object.keys(patch)) {
    const pv = prev?.[k];
    const nv = patch[k];
    if (pv && typeof pv === "object" && nv && typeof nv === "object" && !Array.isArray(pv) && !Array.isArray(nv)) {
      out[k] = deepMergeScores(pv, nv);
    } else {
      out[k] = nv; // replace scalars/arrays
    }
  }
  return out;
}

function extractScoreTuple(sc) {
  if (!sc) return null;

  const g = sc.GOAL || sc.Goal || sc.goal || sc.goals || sc.score;

  // ✅ handle [h,a]
  if (Array.isArray(g)) {
    const h = Number(g[0] ?? 0) || 0;
    const a = Number(g[1] ?? 0) || 0;
    return [h, a];
  }

  // existing object handling
  if (g && typeof g === "object" && (("h" in g && "a" in g) || ("home" in g && "away" in g))) {
    const h = Number(g.h ?? g.home ?? 0) || 0;
    const a = Number(g.a ?? g.away ?? 0) || 0;
    return [h, a];
  }

  return null;
}


function logScoreDiff(prevSc, nextSc) {
  const before = extractScoreTuple(prevSc);
  const after  = extractScoreTuple(nextSc);
  if (!after) return;
  if (!before || before[0] !== after[0] || before[1] !== after[1]) {
    console.log(`[SCORE CHANGED] ${before ? `${before[0]}-${before[1]}` : "—"} → ${after[0]}-${after[1]}`);
  }
}

function ensureGoalPair(sc, fallbackScoreStr, fallbackPair) {
  if (!sc || typeof sc !== "object") sc = {};

  // already good
  if (Array.isArray(sc.GOAL)) return sc;

  // scoreline.to {home,away}
  const to = sc.scoreline && sc.scoreline.to;
  if (to && to.home != null && to.away != null) {
    return { ...sc, GOAL: [Number(to.home) || 0, Number(to.away) || 0] };
  }

  // currScorePair {home,away}
  const cp = sc.currScorePair;
  if (cp && cp.home != null && cp.away != null) {
    return { ...sc, GOAL: [Number(cp.home) || 0, Number(cp.away) || 0] };
  }

  // currScore "2:1" or "2-1"
  const s = String(fallbackScoreStr ?? "");
  const m = s.match(/(\d+)\s*[:\-]\s*(\d+)/);
  if (m) return { ...sc, GOAL: [Number(m[1]), Number(m[2])] };

  // last fallback pair (hs/as etc)
  if (Array.isArray(fallbackPair)) return { ...sc, GOAL: fallbackPair };

  return sc;
}
function toPair(x) {
  if (Array.isArray(x) && x.length >= 2) {
    const h = Number(x[0]), a = Number(x[1]);
    return [Number.isFinite(h) ? h : 0, Number.isFinite(a) ? a : 0];
  }
  if (x && typeof x === "object") {
    const h = Number(x.home ?? x.h ?? x.t1 ?? 0);
    const a = Number(x.away ?? x.a ?? x.t2 ?? 0);
    return [Number.isFinite(h) ? h : 0, Number.isFinite(a) ? a : 0];
  }
  return [0, 0];
}

function parseScoreStr(s) {
  const m = String(s ?? "").match(/(\d+)\s*[:\-]\s*(\d+)/);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

// Takes merged sc object and guarantees GOAL/H1 are [h,a]
function normalizeScoresForGrid(mergedSc, prevGame, rest) {
  const sc = (mergedSc && typeof mergedSc === "object") ? { ...mergedSc } : {};

  // ---- GOAL ----
  let goal = null;

  if (Array.isArray(sc.GOAL)) goal = toPair(sc.GOAL);

  // from scoreline diff shape: { scoreline: { to:{home,away} } }
  if (!goal && sc.scoreline && sc.scoreline.to) goal = toPair(sc.scoreline.to);

  // from stats diff shape: { stats: { IGoal: { to:{home,away} } } }
  if (!goal && sc.stats && sc.stats.IGoal && sc.stats.IGoal.to) goal = toPair(sc.stats.IGoal.to);

  // from receiver “always append”
  if (!goal && rest && rest.currScorePair) goal = toPair(rest.currScorePair);
  if (!goal && rest && rest.currScore) goal = parseScoreStr(rest.currScore);

  // from previous state
  if (!goal && prevGame && prevGame.sc && Array.isArray(prevGame.sc.GOAL)) goal = toPair(prevGame.sc.GOAL);

  // fallback hs/as
  if (!goal) goal = [Number(prevGame?.hs ?? 0), Number(prevGame?.as ?? 0)];

  sc.GOAL = goal;

  // ---- H1 ----
  // if merged already has H1 keep it; else try stats
  if (Array.isArray(sc.H1)) sc.H1 = toPair(sc.H1);
  else if (sc.stats && sc.stats.IFirstHalfScore && sc.stats.IFirstHalfScore.to) sc.H1 = toPair(sc.stats.IFirstHalfScore.to);
  else if (prevGame?.sc?.H1) sc.H1 = toPair(prevGame.sc.H1);

  return sc;
}

function marketKey(m) {
  const n = String(m?.n ?? "");
  const line = (m?.h ?? m?.l ?? "");
  return `${n}|${String(line)}`;
}

function stampPrevOdds(prevBt = [], nextBt = []) {
  const prevMap = new Map();

  for (const m of (Array.isArray(prevBt) ? prevBt : [])) {
    const mk = marketKey(m);
    for (const o of (Array.isArray(m?.o) ? m.o : [])) {
      const ok = `${mk}|${optionKey(o)}`;
      const pv = Number(o?.v);
      if (Number.isFinite(pv) && pv > 0) prevMap.set(ok, pv);
    }
  }

  return (Array.isArray(nextBt) ? nextBt : []).map(m => {
    const mk = marketKey(m);
    const nextOpts = (Array.isArray(m?.o) ? m.o : []).map(o => {
      const ok = `${mk}|${optionKey(o)}`;
      const prevV = prevMap.get(ok);
      const currV = Number(o?.v);

      return {
        ...o,
        v: currV,
        // ✅ only set lv if prevV exists and is > 0
        lv: (Number.isFinite(prevV) && prevV > 0) ? prevV : o?.lv,
      };
    });

    return { ...m, o: nextOpts };
  });
}


	  
// ✅ updateEvent with AP-style market updates
function updateEvent(json,hooks = {}) {
  if (!json) return;
  const id = String(json.std ?? json.id ?? "");
  if (!id) return;

  const idx = events.findIndex(e => {
    const g = e?.obj?.[0];
    return String(g?.std) === id || String(g?.matchId) === id;
  });


  if (idx === -1) {
    const known = events.slice(0, 25).map(e => String(e?.obj?.[0]?.std));

    // optional hook to log raw too (from socket)
    if (typeof hooks.onUnknown === "function") {
      hooks.onUnknown(id, known);
    }
    return;
  }
 // if (idx === -1) return;

  const prevEvent = events[idx];
  const prevGame  = prevEvent.obj?.[0] || {};

  const { sc: scPatch0, scores: scoresPatch0, bt: btPatch, btReplace, ...rest } = json;
const scPatch = (scPatch0 !== undefined) ? scPatch0 : scoresPatch0;
const nextGame = { ...prevGame, ...rest };
nextGame._version = (prevGame._version || 0) + 1;


// ---- scores merge ----
let effectiveScPatch = scPatch;

if (effectiveScPatch === undefined) {
  if (rest.currScorePair || rest.currScore || rest.stats) {
    effectiveScPatch = {
      ...(rest.currScorePair
        ? { GOAL: [rest.currScorePair.home, rest.currScorePair.away] }
        : {}),
      ...(typeof rest.currScore === "string"
        ? { currScore: rest.currScore }
        : {}),
      ...(rest.stats ? { stats: rest.stats } : {}),
    };
  }
}

if (effectiveScPatch !== undefined) {
  const beforeGoal = prevGame?.sc?.GOAL;

  const merged0 = deepMergeScores(prevGame.sc || prevGame.scores || {}, effectiveScPatch);
  const merged  = normalizeScoresForGrid(merged0, prevGame, rest);

  const afterGoal = merged?.GOAL;

  // ✅ LOG: whenever GOAL changes
  const changed =
    Array.isArray(afterGoal) &&
    (!Array.isArray(beforeGoal) ||
      beforeGoal[0] !== afterGoal[0] ||
      beforeGoal[1] !== afterGoal[1]);

  if (changed) {
      nextGame.goalFlashUntil = Date.now() + 8000; // 8 seconds
  nextGame.goalMessage = "GOAL!";
  }

  nextGame.sc = merged;
  nextGame.scores = merged;
  nextGame.hs = merged.GOAL[0];
  nextGame.as = merged.GOAL[1];
  nextGame.tss = `${merged.GOAL[0]}-${merged.GOAL[1]}`;
  nextGame.score = nextGame.tss;
}




  // ---- markets / odds merge ----
  if (Array.isArray(btPatch)) {
    const prevBt = prevGame.bt || [];
    let mergedBt;

    if (btReplace) {
      // full snapshot
      mergedBt = btPatch.slice();
    } else {
      // incremental merge
      mergedBt = [...prevBt];
      const getLine = m => (m.h ?? m.l ?? null);

      btPatch.forEach(newMarket => {
        const i = mergedBt.findIndex(
          m => m.n === newMarket.n && getLine(m) === getLine(newMarket)
        );
        if (i > -1) {
          mergedBt[i] = mergeMarket(mergedBt[i], newMarket);
        } else {
          mergedBt.push({ ...newMarket });
        }
      });
    }

    // 🔎 log 1X2 only for specific match codes
    const TEST_CODES = new Set([2,41,83,84]);

    // cd might be on json, or prevGame, or nextGame
    const rawCode = json.cd ?? prevGame.cd ?? nextGame.cd;
    const codeNum = Number(rawCode);

    if (Number.isFinite(codeNum) && TEST_CODES.has(codeNum)) {
      const before1x2 = (prevGame.bt || []).find(
        m => String(m.n).toUpperCase() === "1X2"
      );
      const after1x2 = mergedBt.find(
        m => String(m.n).toUpperCase() === "1X2"
      );

      // console.log("[STORE 1X2 MERGE]", {
      //   std: id,
      //   cd: codeNum,
      //   before: before1x2?.o,
      //   after: after1x2?.o,
      // });
    }
 //   const prevIndex = indexPrevOdds(prevGame.bt || []);
 mergedBt = stampPrevOdds(prevBt, mergedBt);
nextGame.bt = mergedBt;
     
  }

  const nextEvent  = { ...prevEvent, obj: [nextGame] };
  const nextEvents = events.slice();
  nextEvents[idx]  = nextEvent;
  events = nextEvents;

  liveGames$.next(events);
}


	  
	return {
		addOrUpdate, removeEvent, updateEvent
	};
})();

export default liveGamesStorage;
